import { useCallback, ReactNode, useState, useRef, ChangeEvent } from 'react';
import Hammer from 'react-hammerjs';
import AvatarEditor from 'react-avatar-editor';
import clamp from 'lodash/clamp';
import { nanoid } from 'nanoid';

import { AnyObject } from 'webclient.constants';
import Range from 'lib/form/range/range';
import { RangeContainer } from 'lib/form/range/range.style';
import {
  ImagePickerMode,
  ImageState,
  DEFAULT_EDITOR_PROPS,
  CropData,
} from 'lib/form/image-picker/image-picker.constants';
import RoundButton from 'approot/shared/round-icon-button/round-icon-button';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import type { ImageCropJsonInput } from '../../../../../gql/schema-types';
import * as Styled from 'lib/form/image-picker/image-picker-thumbnail.style';
import VocabularyImageRegenerateButton from './vocabulary-image-regenerate-button';
import { VocabularyImagePickerBouncer } from './vocabulary-thumbnail.style';
import { LANGUAGE_CODE_EN_US, LanguageCodes } from 'global/constants';
import IconTrash from 'approot/shared/icons/icon-trash';

const randomNumber = nanoid();
export const VOCAB_IMAGE_PICKER_WIDTH = 300;

export const DEFAULT_CROP_DATA: ImageCropJsonInput = {
  canvasData: {
    naturalWidth: 0,
    naturalHeight: 0,
  },
  cropData: {
    x: 0.5,
    y: 0.5,
    width: 0,
    height: 0,
  },
  zoom: 1,
};

type PickerState = {
  pickerWidthPixels: number;
  pickerHeightPixels: number;
  imageReady: boolean;
};

export type Props = {
  imageUrl?: string | null;
  label?: ReactNode;
  cropValue: CropData | undefined;
  isDisabled?: boolean;
  disabledTooltip?: string;
  currentImagePreview?: string;
  onCropChange: (crop: CropData) => void;
  onDeleteImage?: () => void;
  onRegenerateImage?: () => void;
  generateImageLoading: boolean;
  altTextLoading: boolean;
  error: boolean;
  language?: LanguageCodes;
};

const VocabularyImagePickerThumbnail = ({
  label,
  cropValue,
  imageUrl,
  isDisabled,
  disabledTooltip,
  onCropChange,
  onDeleteImage,
  onRegenerateImage,
  currentImagePreview,
  generateImageLoading,
  altTextLoading,
  error,
  language,
}: Props) => {
  const RANGE_MIN = 1; // arbitrary value
  const RANGE_MAX = 5; // arbitrary value
  const RANGE_STEP = 0.1;
  const crop = cropValue;

  const [pickerState, setPickerState] = useState<PickerState>({
    pickerWidthPixels: 0,
    pickerHeightPixels: 0,
    imageReady: false,
  });

  const [imageState, setImageState] = useState<ImageState>({
    imageWidth: 0,
    imageHeight: 0,
    x:
      crop && crop.cropData && crop.cropData.x
        ? crop.cropData.x
        : DEFAULT_CROP_DATA.cropData.x,
    y:
      crop && crop.cropData && crop.cropData.y
        ? crop.cropData.y
        : DEFAULT_CROP_DATA.cropData.y,
    zoom: crop
      ? clamp(crop.zoom, RANGE_MIN, RANGE_MAX)
      : DEFAULT_CROP_DATA.zoom,
  });

  const editor = useRef<AvatarEditor | undefined>(undefined);

  const onImageReady = useCallback(() => {
    setPickerState({
      imageReady: true,
      pickerWidthPixels: VOCAB_IMAGE_PICKER_WIDTH,
      pickerHeightPixels: VOCAB_IMAGE_PICKER_WIDTH,
    });
  }, []);

  const sendNewCropBox = useCallback(
    (
      imageWidth: number,
      imageHeight: number,
      x: number,
      y: number,
      zoom: number
    ) => {
      const newCropBox: CropData = {
        canvasData: {
          naturalWidth: imageWidth,
          naturalHeight: imageHeight,
        },
        cropData: {
          x: x,
          y: y,
          width: pickerState.pickerWidthPixels,
          height: pickerState.pickerHeightPixels,
        },
        zoom,
      };
      onCropChange(newCropBox);
    },
    [
      pickerState.pickerHeightPixels,
      pickerState.pickerWidthPixels,
      onCropChange,
    ]
  );

  const updateZoom = useCallback(
    (newZoom: number) => {
      if (imageState.zoom !== newZoom) {
        setImageState({
          ...imageState,
          zoom: newZoom,
        });
        sendNewCropBox(
          imageState.imageWidth,
          imageState.imageHeight,
          imageState.x,
          imageState.y,
          newZoom
        );
      }
    },
    [imageState, sendNewCropBox]
  );

  const onLoadSuccess = useCallback(
    (imgInfo: AnyObject) => {
      if (!pickerState.imageReady || !imgInfo.resource) return;

      let zoom: number = 1;
      let newX: number = imageState.x;
      let newY: number = imageState.y;

      if (cropValue) {
        if (cropValue.zoom) {
          zoom = cropValue.zoom;
        }

        const { cropData } = cropValue;
        if (cropData) {
          const { x, y } = cropData;
          if (x && y) {
            newX = x;
            newY = y;
          }
        }
      }
      const newState: ImageState = {
        imageWidth: imgInfo.resource.width,
        imageHeight: imgInfo.resource.height,
        x: newX,
        y: newY,
        zoom: clamp(zoom, RANGE_MIN, RANGE_MAX),
      };

      setImageState(newState);

      // In case the user doesn't change anything and press save directly
      // x and y will be undefined and will need to be saved as 0.5
      let { imageWidth, imageHeight, x, y } = newState;
      if ((!x && !y) || (x === 0.5 && y === 0.5)) {
        if (!x) x = 0.5;
        if (!y) y = 0.5;
        zoom = newState.zoom;

        sendNewCropBox(imageWidth, imageHeight, x, y, zoom);
      }
    },
    [pickerState, cropValue, imageState, sendNewCropBox]
  );

  const onImageChange = useCallback(() => {
    if (pickerState.imageReady && editor?.current?.state?.image) {
      const { x: editorX, y: editorY } = editor.current.state.image;
      const stateX = imageState.x;
      const stateY = imageState.y;

      // We don't want to refresh if values are the same or if === 0.5 (cf comment below)
      if (
        (editorX !== stateX || editorY !== stateY) &&
        (editorX !== 0.5 || editorY !== 0.5)
      ) {
        setImageState({
          ...imageState,
          x: editorX,
          y: editorY,
        });

        const { imageWidth, imageHeight, zoom } = imageState;
        sendNewCropBox(imageWidth, imageHeight, editorX, editorY, zoom);
      }
    }
  }, [imageState, pickerState, sendNewCropBox]);

  const handleRangeChange = useCallback(
    (changeEvent: ChangeEvent<HTMLInputElement>) => {
      const zoom = parseFloat(changeEvent.target.value);
      updateZoom(zoom);
    },
    [updateZoom]
  );

  const zoomOut = useCallback(() => {
    const newZoom = clamp(imageState.zoom - RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  const zoomIn = useCallback(() => {
    const newZoom = clamp(imageState.zoom + RANGE_STEP, RANGE_MIN, RANGE_MAX);
    updateZoom(newZoom);
  }, [imageState.zoom, updateZoom]);

  const setEditorRef = useCallback((avatarEditor: AvatarEditor) => {
    editor.current = avatarEditor;
  }, []);

  const getEditorStyle = useCallback(
    (mode: string) => {
      const textAlign = !pickerState.imageReady ? 'center' : 'left';
      return {
        borderRadius: '8px',
        margin: '0px auto',
        cursor: 'move',
        display:
          mode === ImagePickerMode.Editor && !pickerState.imageReady
            ? 'none'
            : 'block',
        textAlign,
      };
    },
    [pickerState.imageReady]
  );

  const renderEditor = useCallback(() => {
    const editorStyle = getEditorStyle('editor');
    //by default pinch is disabled for react-hammer
    const hammerOptions = {
      recognizers: {
        pinch: { enable: true },
      },
    };

    return (
      <Hammer options={hammerOptions} onPinchIn={zoomOut} onPinchOut={zoomIn}>
        <Styled.ImagePickerEditor
          $width={VOCAB_IMAGE_PICKER_WIDTH}
          $height={VOCAB_IMAGE_PICKER_WIDTH}
          data-selector="square-image-container"
        >
          {generateImageLoading ? (
            <VocabularyImagePickerBouncer />
          ) : (
            <>
              {onDeleteImage && pickerState.imageReady && (
                <Styled.ImagePickerActions>
                  <TooltipWrapper
                    multiline={!!disabledTooltip}
                    hoverText={
                      isDisabled && disabledTooltip
                        ? disabledTooltip
                        : 'Remove Image'
                    }
                  >
                    <RoundButton
                      data-selector="delete-image"
                      aria-label={
                        isDisabled && disabledTooltip
                          ? disabledTooltip
                          : 'Remove Image'
                      }
                      icon={<IconTrash width={24} height={24} />}
                      disabled={isDisabled || altTextLoading}
                      onClick={onDeleteImage}
                    />
                  </TooltipWrapper>
                </Styled.ImagePickerActions>
              )}
              <AvatarEditor
                {...DEFAULT_EDITOR_PROPS}
                ref={setEditorRef}
                key={imageUrl || currentImagePreview}
                image={imageUrl || currentImagePreview || ''}
                position={{ x: imageState.x, y: imageState.y }}
                width={pickerState.pickerWidthPixels}
                height={pickerState.pickerHeightPixels}
                scale={imageState.zoom}
                style={editorStyle}
                onPositionChange={onImageChange}
                onImageChange={onImageChange}
                onLoadSuccess={onLoadSuccess}
                onImageReady={onImageReady}
              />
            </>
          )}
        </Styled.ImagePickerEditor>
      </Hammer>
    );
  }, [
    imageUrl,
    disabledTooltip,
    isDisabled,
    getEditorStyle,
    imageState,
    onImageChange,
    onImageReady,
    onLoadSuccess,
    pickerState,
    setEditorRef,
    currentImagePreview,
    zoomIn,
    zoomOut,
    onDeleteImage,
    generateImageLoading,
    altTextLoading,
  ]);

  return (
    <div>
      {label}

      <Styled.ImagePickerContainer>
        {renderEditor()}
        {isDisabled && <Styled.ImagePickerDisabledOverlay />}
      </Styled.ImagePickerContainer>

      {onRegenerateImage && language === LANGUAGE_CODE_EN_US && (
        <VocabularyImageRegenerateButton
          onClick={onRegenerateImage}
          disabled={generateImageLoading || altTextLoading}
          error={error}
          label="Regenerate AI Image ✨"
          loading={generateImageLoading}
          data-selector="btn-regenerate-ai-image"
        />
      )}

      {(pickerState.imageReady || isDisabled) && (
        <RangeContainer>
          <Range
            id={randomNumber}
            handleChange={handleRangeChange}
            value={isNaN(imageState.zoom) ? RANGE_MIN : imageState.zoom}
            min={RANGE_MIN}
            max={RANGE_MAX}
            isDisabled={isDisabled || altTextLoading || generateImageLoading}
            step={RANGE_STEP}
            onDecrement={zoomOut}
            onIncrement={zoomIn}
          />

          <Styled.ImagePickerHelpText htmlFor={`range-slider-${randomNumber}`}>
            Drag the image to reposition it and use the slider to scale.
          </Styled.ImagePickerHelpText>
        </RangeContainer>
      )}
    </div>
  );
};

export default VocabularyImagePickerThumbnail;
